import { useTranslation } from "@/i18n/client";
import { Stack } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { Tabs as MantineTabs } from "@mantine/core";
import Tabs from "@/components/Tabs";
import { RadioCheckboxTick } from "@/components/Icons";
import { InputTooltip } from "@/components/InputTooltip";
import Input from "@/components/Input";
import AddressAutosuggest from "@/screens/common/AddressAutosuggest";
import ForeignAddress from "../../Customer/Address/ForeignAddress";
import { AddressFormValues } from "@/screens/Customer/Address/form";
import { useContractState } from "@/hooks/useContractState";
import { Process } from "@/types/contract";
import { isTCRProcessDeath } from "@/utils/contract";
import { isHousehold } from "@/utils/customer";
import { useIsContractRequest } from "@/hooks/useIsContractRequest";

interface PostalAddressProps {
  form: UseFormReturnType<AddressFormValues>;
  showForeignPostalAddress: boolean;
}

export const PostalAddress = ({ form, showForeignPostalAddress }: PostalAddressProps) => {
  const { t } = useTranslation(["address", "hints"]);
  const { contract } = useContractState();
  const isMSProcess = contract.process === Process.MOBILITY_SERVICES;
  const isContractRequest = useIsContractRequest();
  const isPSProcessRequest = contract.process === Process.PS && isContractRequest;

  const isPSDeath = isPSProcessRequest && contract.processReason === "02";
  const isB2BTCRandPSRequest =
    isContractRequest && (contract.process === Process.TCR || contract.process === Process.PS) && !isHousehold(contract.customer);
  const isPSContract = contract.process === Process.PS && !isContractRequest;
  const postalAddressTooltip = isPSDeath
    ? null
    : isB2BTCRandPSRequest
    ? t("hints:customer.addressPostalB2B")
    : isPSContract
    ? t("hints:customer.addressPostalPSContract")
    : t("hints:customer.addressPostal");
  const showToHandsInput = isPSDeath || isTCRProcessDeath(contract);
  const activePostalTab = form.values.isPostalForeign ? "isPostalForeign" : "CZ";

  return (
    <Tabs
      value={activePostalTab}
      onChange={value => {
        if (value !== null && value !== activePostalTab) {
          const isPostalForeign = value !== "CZ";
          form.setValues({
            isPostalForeign: isPostalForeign,
          });
        }
      }}
      defaultValue="CZ">
      <MantineTabs.List>
        <MantineTabs.Tab value="CZ" disabled={!isMSProcess} leftSection={<RadioCheckboxTick fontSize={18} />}>
          {t("czechAddress")}
        </MantineTabs.Tab>
        {showForeignPostalAddress && (
          <MantineTabs.Tab value="isPostalForeign" leftSection={<RadioCheckboxTick data-testid="tickForeign" fontSize={18} />}>
            {t("foreignAddress")}
          </MantineTabs.Tab>
        )}
      </MantineTabs.List>
      <MantineTabs.Panel value="CZ" key={`czech-${activePostalTab}-czechAddress`}>
        <Stack gap={24} mt={24}>
          <InputTooltip
            showTooltip={!!postalAddressTooltip}
            tooltipProps={{
              label: postalAddressTooltip,
            }}>
            <AddressAutosuggest label={t("postalAddress")} {...form.getInputProps("POSTAL")} />
          </InputTooltip>
          {showToHandsInput && (
            <InputTooltip
              tooltipProps={{
                label: t("hints:customer.helpNameCareOf"),
              }}>
              <Input label={t("toHands")} {...form.getInputProps("POSTAL.nameCareOf")} pii />
            </InputTooltip>
          )}
        </Stack>
      </MantineTabs.Panel>
      {isMSProcess && (
        <MantineTabs.Panel value="isPostalForeign" key={`foreign-${activePostalTab}-foreignAddress`}>
          <Stack gap={24} mt={24}>
            <ForeignAddress form={form} addressType="POSTAL_FOREIGN" />
          </Stack>
        </MantineTabs.Panel>
      )}
    </Tabs>
  );
};
